import { Injectable } from '@angular/core';

//
// Port of the FIRST (working) executeHash() in startFunction's success handler (gmr-app.js) plus
// the bookmark-writing click handler in startParameters. There is a SECOND executeHash() defined
// inside startParameters that is dead today: it calls `$.each($.urlParam, ...)`, iterating a
// function's own enumerable properties (there are none) rather than calling $.urlParam(name) for
// each known field, so its callback body never runs. Not ported - see migration plan.
//
@Injectable({ providedIn: 'root' })
export class HashStateService
{
  parsePresetAndDivisions(): { preset: string | null; divisionCodes: string[] }
  {
    if (!window.location.hash)
    {
      return { preset: null, divisionCodes: [] };
    }

    const preset = this.urlParam('preset');
    const divisionsFromBookmark = this.urlParam('division_codes');
    const divisionCodes = divisionsFromBookmark && divisionsFromBookmark.replace(/,/g, '').length > 0
      ? divisionsFromBookmark.split(',')
      : [];

    return { preset, divisionCodes };
  }

  //
  // Deliberately decodeURIComponent, NOT decodeURI (the original's $.urlParam uses decodeURI).
  // decodeURI intentionally leaves reserved delimiter characters - including a comma encoded as
  // %2C - untouched, so a bookmarked multi-value field like division_codes=102673%2C868056 would
  // decode to the literal string "102673%2C868056", still containing "%2C" instead of ",", and
  // the later .split(',') would then see no real comma and return one garbled code instead of
  // two. This is a genuine fix over the original's own $.urlParam (which has the same bug), not a
  // preserved AngularJS quirk - decodeURIComponent is the correct function for decoding a single
  // extracted parameter VALUE, decodeURI is meant for a whole URI where those characters must
  // stay structural.
  //
  private urlParam(name: string): string | null
  {
    const results = new RegExp('[#&]' + name + '=([^&#]*)').exec(window.location.href);
    if (results == null)
    {
      return null;
    }
    return decodeURIComponent(results[1]) || null;
  }

  //
  // 1:1 port of $.formValues (startParameters, gmr-app.js) - used to restore a single parameter's
  // value from the bookmark hash. Deliberately uses `[?&]`, not `[#&]`, matching the original
  // exactly: it only ever gets called with a parameter's cfr_code, never with 'preset' (which is
  // always the first entry right after the `#`), so the missing `#` branch never actually matters
  // for how this is used - see parsePresetAndDivisions above for the one that does need it.
  //
  formValue(name: string): string | null
  {
    const results = new RegExp('[?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (results == null)
    {
      return null;
    }
    // See urlParam() above for why decodeURIComponent, not the original's decodeURI.
    return decodeURIComponent(results[1]) || null;
  }

  //
  // Port of the copyClipboard/requestButton.download click handler in startParameters: serializes
  // the parameter form (plus explicit `false` entries for unchecked checkboxes, since a native
  // form serialization omits unchecked checkboxes entirely) into the URL hash for bookmarking.
  //
  writeBookmark(formElement: HTMLFormElement): string
  {
    const entries = Array.from(new FormData(formElement).entries()) as [string, string][];
    const params = new URLSearchParams(entries);
    formElement.querySelectorAll<HTMLInputElement>('input[type=checkbox]').forEach(checkbox =>
    {
      if (!checkbox.checked)
      {
        params.append(checkbox.name, 'false');
      }
    });
    const formData = params.toString();
    window.location.hash = formData;
    return formData;
  }
}
